"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
    loadUserSettings,
    saveUserSettings,
    loadSettingsFromLocalStorage,
    saveSettingsToLocalStorage,
    migrateLocalSettingsToFirebase,
    defaultSettings as importedDefaults
} from './user-settings-firebase';
import { defaultDrillCardPreferences } from '@/components/drill-library/types/drill-library-settings';
import type { AppSettings, Unit } from '../types/settings';

// Re-export types for convenience
export type { AppSettings, Unit, MaxWidthOption, AiProvider, DrillFilters } from '../types/settings';

// Define the shape of the context value
interface SettingsContextType {
    settings: AppSettings;
    setSettings: React.Dispatch<React.SetStateAction<AppSettings>>;
    unit: Unit; // For backward compatibility and ease of use
    setUnit: (unit: Unit) => void;
    isLoading: boolean;
}

const getDefaultUnit = (): Unit => {
    if (typeof navigator !== 'undefined') {
        const language = navigator.language;
        // Locales that commonly use inches
        const inchLocales = ['en-US', 'en-GB', 'en-LR', 'en-MM'];
        if (inchLocales.some(locale => language.startsWith(locale))) {
            return 'inches';
        }
    }
    // Default to metric (cm) for others
    return 'cm';
};

// Use imported defaults but override unit with locale-specific default
const defaultSettings: AppSettings = {
    ...importedDefaults,
    unit: getDefaultUnit(),
    drillCardPreferences: {
        ...defaultDrillCardPreferences,
        ...(importedDefaults.drillCardPreferences ?? {}),
    },
};

// Create the context
/** Exported for optional-hooks.ts — prefer useSettings() in normal usage. */
export const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

// Create a provider component
export const SettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<AppSettings>(defaultSettings);
    const [isLoading, setIsLoading] = useState(true);
    const { user, loading: authLoading } = useAuth();
    const lastUserIdRef = useRef<string | null>(null);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [persistState, setPersistState] = useState<'idle' | 'pending' | 'saved' | 'error'>('idle');
    const [savedExperimental, setSavedExperimental] = useState<'unknown' | 'on' | 'off'>('unknown');

    // Load settings when component mounts or user changes
    useEffect(() => {
        let isMounted = true;

        // Wait for auth to initialize before trying to load settings
        if (authLoading) return;

        const loadSettings = async () => {
            setIsLoading(true);

            if (user?.uid) {
                // User is authenticated, load from Firebase
                const userSettings = await loadUserSettings(user.uid);

                if (isMounted) {
                    setSettings(userSettings);

                    // If this is a new login (not a refresh), migrate local settings if needed
                    if (lastUserIdRef.current === null) {
                        await migrateLocalSettingsToFirebase(user.uid);
                    }
                    lastUserIdRef.current = user.uid;
                }
            } else {
                // No user, load from localStorage
                const localSettings = loadSettingsFromLocalStorage();
                if (isMounted) {
                    setSettings(localSettings);
                    lastUserIdRef.current = null;
                }
            }

            if (isMounted) {
                setIsLoading(false);
            }
        };

        loadSettings();

        return () => {
            isMounted = false;
        };
    }, [user?.uid, authLoading]);

    // Save settings when they change (debounced)
    useEffect(() => {
        // Do not save if we are still loading settings or auth
        if (isLoading || authLoading) return;

        let cancelled = false;
        setPersistState('pending');

        // Clear any existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce saves to avoid too many writes
        saveTimeoutRef.current = setTimeout(async () => {
            const experimental = settings.experimentalFeaturesEnabled === true ? 'on' : 'off';
            if (user?.uid) {
                const saved = await saveUserSettings(user.uid, settings);
                if (cancelled) return;
                setPersistState(saved ? 'saved' : 'error');
                if (saved) {
                    setSavedExperimental(experimental);
                }
            } else {
                saveSettingsToLocalStorage(settings);
                if (cancelled) return;
                setPersistState('saved');
                setSavedExperimental(experimental);
            }
        }, 1000); // 1 second debounce

        // Cleanup on unmount
        return () => {
            cancelled = true;
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [settings, user?.uid, isLoading, authLoading]);

    const setUnit = (newUnit: Unit) => {
        setSettings(s => ({ ...s, unit: newUnit }));
    };

    return (
        <SettingsContext.Provider value={{ settings, setSettings, unit: settings.unit, setUnit, isLoading }}>
            <span
                data-testid="settings-persist-state"
                data-state={persistState}
                data-experimental={savedExperimental}
                hidden
            />
            {children}
        </SettingsContext.Provider>
    );
};

// Create a custom hook for easy access to the context
export function useSettings() {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
}

// Function to get settings outside of a React component
// Note: This only reads from localStorage (not Firebase) as it's for non-React contexts
export const getAppSettings = (): AppSettings => {
    if (typeof window === 'undefined') {
        return defaultSettings;
    }
    return loadSettingsFromLocalStorage();
};

